import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Download,
  History,
  Minus,
  Plus,
  Power,
  Search,
  Sliders,
  X,
} from 'lucide-react';
import { supabase } from '../services/supabase';
import { etiquetaVariante } from '../lib/variantes';
import {
  filasDeInventario,
  filasDeVentas,
  textoDelPrecio,
  type MovimientoDeStock,
} from '../lib/inventarioExcel';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import { hoy } from '../lib/fechaLocal';

// Inventario por variante (mig 214). Solo productos físicos: el resto no lleva
// existencias.
//
// El stock NO se escribe desde aquí. La página llama a `adjust_stock`, que es
// quien exige el motivo y deja el movimiento; el trigger de guarda de la mig
// 214 rechaza cualquier UPDATE directo. Si un día alguien "arregla" esta
// página escribiendo la columna, se encontrará con un 0A000, que es
// exactamente lo que queremos que pase.
//
// El negativo es el dato, no un error: "debo 3 pantalones L" es lo que hay que
// pedirle al proveedor. Por eso la franja de arriba y por eso el rojo.

interface Variante {
  id: string;
  attributes: Record<string, string>;
  stock: number;
  position: number;
  active: boolean;
  sku: string | null;
}

interface Producto {
  id: string;
  name: string;
  active: boolean;
  variantes: Variante[];
}

interface Movimiento {
  id: string;
  delta: number;
  balance_after: number;
  reason: string;
  note: string | null;
  order_id: string | null;
  created_at: string;
  actor: { full_name: string } | null;
}

const MOTIVO_LABEL: Record<string, string> = {
  sale: 'Venta',
  manual: 'Ajuste a mano',
  initial: 'Stock inicial',
  correction: 'Corrección',
};

// Se movió a `lib/variantes.ts` cuando la pantalla de regalar necesitó nombrar
// las mismas prendas. Se sigue reexportando: `inventario.test.tsx` la importa
// desde aquí, y la prueba es de la función, no de dónde vive.
export { etiquetaVariante };

function esNegativa(v: Variante): boolean {
  return v.stock < 0;
}

export default function InventarioPage() {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [soloAgotadas, setSoloAgotadas] = useState(false);

  // Ajuste
  const [ajustando, setAjustando] = useState<{ producto: Producto; variante: Variante } | null>(
    null
  );
  const [delta, setDelta] = useState(1);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorAjuste, setErrorAjuste] = useState<string | null>(null);

  // Historial
  const [historialDe, setHistorialDe] = useState<{ producto: Producto; variante: Variante } | null>(
    null
  );
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  // Alta de variante
  const [creandoEn, setCreandoEn] = useState<Producto | null>(null);
  const [ejes, setEjes] = useState<{ eje: string; valor: string }[]>([]);
  const [errorAlta, setErrorAlta] = useState<string | null>(null);
  // Activar/desactivar una variante es una acción de fila: recargaba el
  // listado sin mirar el resultado, así que un rechazo parecía "no ha pasado
  // nada" y la variante seguía como estaba.
  const [listError, setListError] = useState('');
  // La lista de la compra sale de lo que se lea aquí. Con la consulta caída,
  // "no hay nada rojo" y "no se ha podido mirar" son la misma pantalla: el
  // club no llama al proveedor y la talla se agota.
  const [errorCarga, setErrorCarga] = useState('');
  // Y un historial vacío se lee como "esta variante no se ha movido nunca",
  // que es justo lo que se mira para cuadrar un descuadre de stock.
  const [errorHistorial, setErrorHistorial] = useState('');

  // Baja
  const [desactivando, setDesactivando] = useState<Variante | null>(null);

  // Descarga
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('id, name, active, product_variants(id, attributes, stock, position, active, sku)')
      .eq('fulfillment_type', 'physical_stock')
      .order('name');

    setErrorCarga(error ? `No se pudo cargar el inventario: ${error.message}` : '');
    const filas = (data ?? []) as unknown as (Producto & { product_variants: Variante[] })[];
    setProductos(
      filas.map((p) => ({
        id: p.id,
        name: p.name,
        active: p.active,
        // El orden lo da `position`, no el que devuelva el embed.
        variantes: [...(p.product_variants ?? [])].sort((a, b) => a.position - b.position),
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ── La descarga en Excel ─────────────────────────────────────────────────
  // Dos hojas: `Inventario` con sus totales, y `Ventas` al lado. Las columnas
  // de la primera son las razones del libro —inicial, ajustes, vendidas,
  // regaladas, devueltas— para que `inicial + ajustes − vendidas − regaladas +
  // devueltas = stock` se pueda comprobar en Excel, fila a fila. Con solo
  // «vendidas» habría que restar a mano para saber si el stock es el que dice.
  async function descargarExcel() {
    setDescargando(true);
    setErrorDescarga('');

    // ⚠️ `orders` TIENE CUATRO CLAVES AJENAS A `profiles` —`user_id`,
    // `paid_by_profile_id`, `gifted_by` y `paid_confirmed_by`—. Un embed sin
    // nombrar la clave sale con PGRST201, que es exactamente lo que dejó
    // `/pedidos` en blanco. `comprador` la nombra. Las otras dos relaciones
    // (`stock_movements → orders` y `stock_movements → product_variants`) hoy
    // son únicas, y van nombradas igual para que nadie tenga que volver a
    // comprobar cuál de las dos cosas era.
    //
    // Se piden TODOS los movimientos, no solo los de los productos que esta
    // pantalla lista: la hoja 1 es el inventario de hoy (stock físico), pero la
    // hoja 2 es el histórico, y si un producto dejara de ser `physical_stock`
    // sus ventas no deben desaparecer del histórico. Por eso el nombre del
    // artículo viaja en el embed y no se busca en lo ya cargado.
    const { data, error, count } = await supabase
      .from('stock_movements')
      .select(
        `variant_id, delta, reason, note, created_at,
         variante:product_variants!stock_movements_variant_id_fkey(
           attributes, sku, producto:products(name)
         ),
         pedido:orders!stock_movements_order_id_fkey(
           order_number, items, payment_method,
           comprador:profiles!orders_user_id_fkey(full_name)
         )`,
        // `count` no se enseña: sirve para saber si PostgREST ha cortado. Ver
        // el `if` de más abajo.
        { count: 'exact' }
      )
      .order('created_at', { ascending: false });

    // Una descarga que sale vacía porque la lectura no entró es peor que no
    // descargar: Jose se queda con un Excel que parece decir que no se ha
    // vendido nada. Aquí se para y se dice.
    if (error) {
      setErrorDescarga(`No se pudo leer el libro de movimientos: ${error.message}`);
      setDescargando(false);
      return;
    }

    const movimientos = (data ?? []) as unknown as MovimientoDeStock[];

    // ⚠️ Y UN EXCEL A MEDIAS TAMBIÉN MIENTE. PostgREST corta las respuestas por
    // `max-rows`, un tope del servidor que esta pantalla no controla ni ve. Hoy
    // el libro tiene 48 filas y no se nota; es un libro que solo crece, y el
    // día que pase del tope la descarga saldría con las primeras N con pinta de
    // estar completa. `count: 'exact'` dice cuántas hay de verdad: se comparan
    // y se para.
    //
    // `count` es `null` cuando la respuesta no trae `Content-Range`. Entonces
    // no hay nada que comparar y no se inventa un fallo.
    if (count !== null && movimientos.length < count) {
      setErrorDescarga(
        `El servidor ha devuelto ${movimientos.length} de ${count} movimientos. ` +
          'No se descarga a medias: un Excel incompleto parece completo.'
      );
      setDescargando(false);
      return;
    }

    try {
      const { default: writeXlsxFile } = await import('write-excel-file/browser');

      const inventario = filasDeInventario(productos, movimientos);
      const ventas = filasDeVentas(movimientos);

      const hojaInventario = [
        [
          { value: 'Producto', fontWeight: 'bold' as const },
          { value: 'Variante', fontWeight: 'bold' as const },
          { value: 'SKU', fontWeight: 'bold' as const },
          { value: 'Stock inicial', fontWeight: 'bold' as const },
          { value: 'Ajustes', fontWeight: 'bold' as const },
          { value: 'Vendidas', fontWeight: 'bold' as const },
          { value: 'Regaladas', fontWeight: 'bold' as const },
          { value: 'Devueltas', fontWeight: 'bold' as const },
          { value: 'Stock hoy', fontWeight: 'bold' as const },
          { value: 'Estado', fontWeight: 'bold' as const },
        ],
        ...inventario.map((f) => [
          { value: f.producto, type: String },
          { value: f.variante, type: String, fontWeight: f.esTotal ? ('bold' as const) : undefined },
          { value: f.sku ?? '', type: String },
          { value: f.inicial, type: Number },
          { value: f.ajustes, type: Number },
          { value: f.vendidas, type: Number },
          { value: f.regaladas, type: Number },
          { value: f.devueltas, type: Number },
          { value: f.stock, type: Number, fontWeight: f.esTotal ? ('bold' as const) : undefined },
          {
            value: f.esTotal
              ? f.productoActivo
                ? 'Producto activo'
                : 'Producto de baja'
              : f.activa
                ? 'Activa'
                : 'De baja',
            type: String,
          },
        ]),
      ];

      const hojaVentas = [
        [
          { value: 'Fecha', fontWeight: 'bold' as const },
          { value: 'Pedido', fontWeight: 'bold' as const },
          { value: 'Artículo', fontWeight: 'bold' as const },
          { value: 'Variante', fontWeight: 'bold' as const },
          { value: 'Unidades', fontWeight: 'bold' as const },
          { value: 'Motivo', fontWeight: 'bold' as const },
          { value: 'Alumno', fontWeight: 'bold' as const },
          { value: 'Precio unidad', fontWeight: 'bold' as const },
          { value: 'Forma de pago', fontWeight: 'bold' as const },
          { value: 'Nota', fontWeight: 'bold' as const },
        ],
        ...ventas.map((v) => [
          { value: v.fecha, type: Date, format: 'dd/mm/yyyy' },
          // ⚠️ `undefined` y no `null`: la celda admite `Value | undefined` y un
          // `null` no typechequea. La diferencia importa además en la hoja: un
          // movimiento sin pedido deja la celda VACÍA, y no un 0 que se leería
          // como «el pedido número 0».
          { value: v.pedido ?? undefined, type: Number },
          { value: v.articulo, type: String },
          { value: v.variante, type: String },
          { value: v.unidades, type: Number },
          { value: v.motivo, type: String },
          { value: v.alumno, type: String },
          // ⚠️ La celda es un NÚMERO cuando hay precio y TEXTO cuando no, y el
          // texto dice POR QUÉ no lo hay: «dentro de un pack» para las que
          // viajaron en un pack (18 de las 32 vendidas hoy), y nunca para las
          // demás. Un 0,00 € sería mentira y una celda vacía parecería un dato
          // que falta.
          v.precio.clase === 'suelto'
            ? { value: v.precio.cents / 100, type: Number, format: '#,##0.00\\ €' }
            : { value: textoDelPrecio(v.precio), type: String },
          { value: v.formaDePago, type: String },
          { value: v.nota ?? '', type: String },
        ]),
      ];

      const anchos = [
        { width: 18 },
        { width: 22 },
        { width: 14 },
        { width: 12 },
        { width: 10 },
        { width: 10 },
        { width: 11 },
        { width: 11 },
        { width: 11 },
        { width: 16 },
      ];
      const anchosVentas = [
        { width: 12 },
        { width: 9 },
        { width: 16 },
        { width: 22 },
        { width: 10 },
        { width: 13 },
        { width: 24 },
        { width: 20 },
        { width: 15 },
        { width: 30 },
      ];

      await writeXlsxFile(
        [
          { sheet: 'Inventario', data: hojaInventario, columns: anchos },
          { sheet: 'Ventas', data: hojaVentas, columns: anchosVentas },
        ],
        {}
      ).toFile(`inventario-vertex-${hoy()}.xlsx`);
    } catch (e) {
      setErrorDescarga(
        `No se pudo generar el Excel: ${e instanceof Error ? e.message : 'error desconocido'}`
      );
    } finally {
      setDescargando(false);
    }
  }

  // La lista de la compra. Es lo primero de la pantalla porque es lo único que
  // exige una acción fuera de la app: llamar al proveedor. Buscar los rojos a
  // ojo entre cuatro productos de seis tallas es 24 filas de barrido.
  const pendienteDePedir = useMemo(() => {
    const filas: { producto: string; variante: string; faltan: number }[] = [];
    for (const p of productos) {
      for (const v of p.variantes) {
        if (esNegativa(v)) {
          filas.push({
            producto: p.name,
            variante: etiquetaVariante(v.attributes),
            faltan: Math.abs(v.stock),
          });
        }
      }
    }
    return filas.sort((a, b) => b.faltan - a.faltan);
  }, [productos]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return productos
      .map((p) => ({
        ...p,
        variantes: p.variantes.filter((v) => {
          if (soloAgotadas && v.stock > 0) return false;
          if (!q) return true;
          return (
            p.name.toLowerCase().includes(q) ||
            etiquetaVariante(v.attributes).toLowerCase().includes(q) ||
            (v.sku ?? '').toLowerCase().includes(q)
          );
        }),
      }))
      .filter((p) => p.variantes.length > 0);
  }, [productos, busqueda, soloAgotadas]);

  function abrirAjuste(producto: Producto, variante: Variante, deltaInicial: number) {
    setAjustando({ producto, variante });
    setDelta(deltaInicial);
    setMotivo('');
    setErrorAjuste(null);
  }

  async function guardarAjuste() {
    if (!ajustando) return;
    if (!motivo.trim()) {
      setErrorAjuste('Escribe el motivo del ajuste.');
      return;
    }
    if (delta === 0) {
      setErrorAjuste('Un ajuste de 0 no cambia nada.');
      return;
    }
    setGuardando(true);
    setErrorAjuste(null);
    const { error } = await supabase.rpc('adjust_stock', {
      p_variant_id: ajustando.variante.id,
      p_delta: delta,
      p_note: motivo.trim(),
    });
    setGuardando(false);
    if (error) {
      setErrorAjuste(error.message);
      return;
    }
    setAjustando(null);
    await cargar();
  }

  async function abrirHistorial(producto: Producto, variante: Variante) {
    setHistorialDe({ producto, variante });
    // Se vacía ANTES de pedir: si no, al abrir el historial de otra variante se
    // ve un instante el de la anterior, que es peor que ver un "Cargando…".
    setMovimientos([]);
    setErrorHistorial('');
    setCargandoHistorial(true);
    // La FK va nombrada (mismo patrón que BonosManualesPage): `profiles` es
    // además el origen de la vista profiles_with_medical_info, y sin el nombre
    // el embed puede quedarse en ambiguo.
    const { data, error } = await supabase
      .from('stock_movements')
      .select(
        `id, delta, balance_after, reason, note, order_id, created_at,
         actor:profiles!stock_movements_actor_id_fkey(full_name)`
      )
      .eq('variant_id', variante.id)
      .order('created_at', { ascending: false });
    setErrorHistorial(error ? `No se pudo leer el historial: ${error.message}` : '');
    setMovimientos((data ?? []) as unknown as Movimiento[]);
    setCargandoHistorial(false);
  }

  function abrirAlta(producto: Producto) {
    setCreandoEn(producto);
    setErrorAlta(null);
    // Los ejes que ya usa el producto, para no obligar a recordarlos. Si no
    // tiene ninguno (producto de una sola opción), se abre vacío.
    const usados = new Set<string>();
    producto.variantes.forEach((v) =>
      Object.keys(v.attributes ?? {}).forEach((k) => usados.add(k))
    );
    setEjes(usados.size > 0 ? [...usados].map((eje) => ({ eje, valor: '' })) : []);
  }

  async function crearVariante() {
    if (!creandoEn) return;
    const limpios = ejes
      .map((e) => ({ eje: e.eje.trim().toLowerCase(), valor: e.valor.trim() }))
      .filter((e) => e.eje !== '' || e.valor !== '');

    if (limpios.some((e) => e.eje === '' || e.valor === '')) {
      setErrorAlta('Cada eje necesita nombre y valor.');
      return;
    }
    if (new Set(limpios.map((e) => e.eje)).size !== limpios.length) {
      setErrorAlta('Hay un eje repetido.');
      return;
    }

    const attributes: Record<string, string> = {};
    limpios.forEach((e) => {
      attributes[e.eje] = e.valor;
    });

    // stock NO se manda: nace a 0 y sube con un ajuste, que deja motivo. Es la
    // regla de la mig 214 y el trigger de guarda la hace cumplir.
    const { error } = await supabase
      .from('product_variants')
      .insert({ product_id: creandoEn.id, attributes });
    if (error) {
      setErrorAlta(
        error.code === '23505' ? 'Esa variante ya existe en este producto.' : error.message
      );
      return;
    }
    setCreandoEn(null);
    await cargar();
  }

  async function cambiarActiva(variante: Variante, activa: boolean) {
    const { error } = await supabase
      .from('product_variants')
      .update({ active: activa })
      .eq('id', variante.id);
    if (error) {
      setListError(`No se pudo ${activa ? 'activar' : 'desactivar'} la variante: ${error.message}`);
      return;
    }
    setListError('');
    await cargar();
  }

  const totalDe = (p: Producto) => p.variantes.reduce((acc, v) => acc + v.stock, 0);

  return (
    <div className="space-y-6" data-testid="inventario-page">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="inventario-list-error"
        >
          {listError}
        </p>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-oswald text-2xl uppercase tracking-wider text-white">Inventario</h1>
          <p className="text-text-secondary text-sm font-inter">
            Existencias por talla y variante. El negativo es lo que hay que pedir.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
            />
            <input
              data-testid="inventario-buscar"
              className="input pl-9"
              placeholder="Buscar producto o talla…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm font-inter text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              data-testid="inventario-solo-agotadas"
              checked={soloAgotadas}
              onChange={(e) => setSoloAgotadas(e.target.checked)}
            />
            Solo sin existencias
          </label>
          <button
            type="button"
            className="btn-gold flex items-center gap-2"
            data-testid="inventario-descargar"
            onClick={descargarExcel}
            disabled={descargando || loading}
          >
            <Download size={16} /> {descargando ? 'Preparando…' : 'Descargar Excel'}
          </button>
        </div>
      </div>

      {errorDescarga && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="inventario-descarga-error"
        >
          {errorDescarga}
        </p>
      )}

      {/* La lista de la compra */}
      {pendienteDePedir.length > 0 && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="inventario-pendiente-proveedor"
        >
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-error" />
            <h2 className="font-oswald text-sm uppercase tracking-widest text-error">
              Pendiente de pedir al proveedor
            </h2>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {pendienteDePedir.map((f, i) => (
              <span key={i} className="font-inter text-sm text-white">
                {f.producto} · {f.variante}
                <span className="text-error font-oswald ml-2">{f.faltan}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {loading && <p className="text-text-secondary font-inter">Cargando…</p>}

      {!loading && errorCarga && (
        <div className="card" data-testid="inventario-ilegible">
          <p className="text-error text-sm">{errorCarga}</p>
          <p className="text-text-secondary text-sm mt-2">
            La lista de lo que hay que pedir al proveedor sale de esta consulta: sin ella no está
            vacía, está sin mirar.
          </p>
          <button
            className="btn-secondary mt-4"
            onClick={cargar}
            data-testid="inventario-reintentar"
          >
            Reintentar
          </button>
        </div>
      )}

      {!loading && !errorCarga && visibles.length === 0 && (
        <p className="text-text-secondary font-inter" data-testid="inventario-vacio">
          No hay ninguna variante que encaje con el filtro.
        </p>
      )}

      {visibles.map((p) => (
        <div key={p.id} className="card" data-testid={`inventario-producto-${p.id}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-oswald text-lg uppercase tracking-wider text-white">
              {p.name}
              {!p.active && (
                <span className="ml-3 text-xs font-inter normal-case text-text-secondary">
                  (producto inactivo)
                </span>
              )}
            </h2>
            <span className="font-inter text-sm text-text-secondary">
              total <span className="text-white font-oswald">{totalDe(p)}</span>
            </span>
          </div>

          <div className="divide-y divide-bg-border">
            {p.variantes.map((v) => (
              <div
                key={v.id}
                className="flex items-center gap-3 py-2.5 flex-wrap"
                data-testid={`variante-${v.id}`}
              >
                <span
                  className={`font-inter text-sm flex-1 min-w-[10rem] ${v.active ? 'text-white' : 'text-text-secondary line-through'}`}
                >
                  {etiquetaVariante(v.attributes)}
                  {v.sku && <span className="text-text-secondary text-xs ml-2">{v.sku}</span>}
                </span>

                <span
                  data-testid={`stock-${v.id}`}
                  className={`font-oswald text-lg w-12 text-right ${esNegativa(v) ? 'text-error' : 'text-white'}`}
                >
                  {v.stock}
                </span>

                <button
                  className="w-8 h-8 rounded-lg border border-bg-border flex items-center justify-center text-text-secondary hover:text-white hover:border-gold-primary/40 transition-colors"
                  onClick={() => abrirAjuste(p, v, -1)}
                  aria-label={`Quitar una unidad de ${etiquetaVariante(v.attributes)}`}
                  data-testid={`menos-${v.id}`}
                >
                  <Minus size={14} />
                </button>
                <button
                  className="w-8 h-8 rounded-lg border border-bg-border flex items-center justify-center text-text-secondary hover:text-white hover:border-gold-primary/40 transition-colors"
                  onClick={() => abrirAjuste(p, v, 1)}
                  aria-label={`Añadir una unidad de ${etiquetaVariante(v.attributes)}`}
                  data-testid={`mas-${v.id}`}
                >
                  <Plus size={14} />
                </button>
                <button
                  className="btn-ghost text-xs flex items-center gap-1.5"
                  onClick={() => abrirAjuste(p, v, 1)}
                  data-testid={`ajustar-${v.id}`}
                >
                  <Sliders size={14} /> Ajustar
                </button>
                <button
                  className="btn-ghost text-xs flex items-center gap-1.5"
                  onClick={() => abrirHistorial(p, v)}
                  data-testid={`historial-${v.id}`}
                >
                  <History size={14} /> Historial
                </button>
                <button
                  className="text-text-secondary hover:text-error transition-colors"
                  onClick={() => (v.active ? setDesactivando(v) : cambiarActiva(v, true))}
                  aria-label={v.active ? 'Desactivar variante' : 'Reactivar variante'}
                  data-testid={`activa-${v.id}`}
                >
                  <Power size={16} />
                </button>
              </div>
            ))}
          </div>

          <button
            className="btn-ghost text-xs mt-4 flex items-center gap-1.5"
            onClick={() => abrirAlta(p)}
            data-testid={`anadir-variante-${p.id}`}
          >
            <Plus size={14} /> Añadir variante
          </button>
        </div>
      ))}

      {/* ─── Ajuste ─── */}
      <Modal
        open={!!ajustando}
        onClose={() => setAjustando(null)}
        title={
          ajustando
            ? `Ajustar ${ajustando.producto.name} · ${etiquetaVariante(ajustando.variante.attributes)}`
            : ''
        }
      >
        {ajustando && (
          <div className="space-y-4">
            <p className="font-inter text-sm text-text-secondary">
              Ahora hay{' '}
              <span className={ajustando.variante.stock < 0 ? 'text-error' : 'text-white'}>
                {ajustando.variante.stock}
              </span>
              . Quedará en{' '}
              <span
                className={
                  ajustando.variante.stock + delta < 0 ? 'text-error' : 'text-gold-primary'
                }
                data-testid="ajuste-resultado"
              >
                {ajustando.variante.stock + delta}
              </span>
              .
            </p>

            <div>
              <label className="label" htmlFor="ajuste-delta">
                Cantidad (negativo para restar)
              </label>
              <input
                id="ajuste-delta"
                data-testid="ajuste-delta"
                type="number"
                className="input"
                value={delta}
                onChange={(e) => setDelta(Number(e.target.value))}
              />
            </div>

            <div>
              <label className="label" htmlFor="ajuste-motivo">
                Motivo (obligatorio)
              </label>
              <textarea
                id="ajuste-motivo"
                data-testid="ajuste-motivo"
                className="input"
                rows={2}
                placeholder="Entrada del proveedor, camiseta regalada, recuento…"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
              <p className="text-text-secondary text-xs font-inter mt-1">
                Queda registrado con tu nombre y la fecha. Es lo que explica el saldo dentro de seis
                meses.
              </p>
            </div>

            {errorAjuste && (
              <p className="text-error text-sm font-inter" data-testid="ajuste-error">
                {errorAjuste}
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button className="btn-ghost" onClick={() => setAjustando(null)}>
                Cancelar
              </button>
              <button
                className="btn-gold"
                onClick={guardarAjuste}
                disabled={guardando}
                data-testid="ajuste-guardar"
              >
                {guardando ? 'Guardando…' : 'Guardar ajuste'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ─── Historial ─── */}
      <Modal
        open={!!historialDe}
        onClose={() => setHistorialDe(null)}
        title={
          historialDe
            ? `Historial · ${historialDe.producto.name} · ${etiquetaVariante(historialDe.variante.attributes)}`
            : ''
        }
        size="lg"
      >
        {cargandoHistorial && <p className="text-text-secondary font-inter">Cargando…</p>}
        {!cargandoHistorial && errorHistorial && (
          <p className="text-error text-sm font-inter" data-testid="historial-ilegible">
            {errorHistorial}. No quiere decir que la variante no se haya movido nunca.
          </p>
        )}
        {!cargandoHistorial && !errorHistorial && movimientos.length === 0 && (
          <p className="text-text-secondary font-inter" data-testid="historial-vacio">
            Esta variante no tiene movimientos todavía.
          </p>
        )}
        {!cargandoHistorial && movimientos.length > 0 && (
          <table className="w-full text-sm font-inter" data-testid="historial-tabla">
            <thead>
              <tr className="text-text-secondary text-left border-b border-bg-border">
                <th className="py-2">Fecha</th>
                <th className="py-2 text-right">Cambio</th>
                <th className="py-2 text-right">Saldo</th>
                <th className="py-2">Motivo</th>
                <th className="py-2">Quién</th>
              </tr>
            </thead>
            <tbody>
              {movimientos.map((m) => (
                <tr key={m.id} className="border-b border-bg-border/60">
                  <td className="py-2 text-text-secondary whitespace-nowrap">
                    {new Date(m.created_at).toLocaleDateString('es-ES', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </td>
                  <td
                    className={`py-2 text-right font-oswald ${m.delta < 0 ? 'text-error' : 'text-success'}`}
                  >
                    {m.delta > 0 ? `+${m.delta}` : m.delta}
                  </td>
                  <td
                    className={`py-2 text-right font-oswald ${m.balance_after < 0 ? 'text-error' : 'text-white'}`}
                  >
                    {m.balance_after}
                  </td>
                  <td className="py-2 text-white">
                    {MOTIVO_LABEL[m.reason] ?? m.reason}
                    {m.note && <span className="text-text-secondary"> · {m.note}</span>}
                    {m.order_id && (
                      <Link
                        to={`/pedidos?pedido=${m.order_id}`}
                        className="text-gold-primary hover:underline ml-2"
                        data-testid={`historial-pedido-${m.id}`}
                      >
                        ver pedido
                      </Link>
                    )}
                  </td>
                  <td className="py-2 text-text-secondary">{m.actor?.full_name ?? 'El sistema'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>

      {/* ─── Alta de variante ─── */}
      <Modal
        open={!!creandoEn}
        onClose={() => setCreandoEn(null)}
        title={creandoEn ? `Nueva variante de ${creandoEn.name}` : ''}
      >
        <div className="space-y-4">
          <p className="font-inter text-sm text-text-secondary">
            Un eje es una dimensión: talla, color, capacidad. Sin ejes, el producto tiene una sola
            variante. Nace con 0 existencias: se suben con un ajuste, que deja motivo.
          </p>

          {ejes.map((e, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                className="input flex-1"
                placeholder="eje (talla)"
                value={e.eje}
                data-testid={`alta-eje-${i}`}
                onChange={(ev) =>
                  setEjes((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, eje: ev.target.value } : x))
                  )
                }
              />
              <input
                className="input flex-1"
                placeholder="valor (XXXL)"
                value={e.valor}
                data-testid={`alta-valor-${i}`}
                onChange={(ev) =>
                  setEjes((prev) =>
                    prev.map((x, j) => (j === i ? { ...x, valor: ev.target.value } : x))
                  )
                }
              />
              <button
                className="text-text-secondary hover:text-error transition-colors"
                onClick={() => setEjes((prev) => prev.filter((_, j) => j !== i))}
                aria-label="Quitar eje"
              >
                <X size={16} />
              </button>
            </div>
          ))}

          <button
            className="btn-ghost text-xs flex items-center gap-1.5"
            onClick={() => setEjes((prev) => [...prev, { eje: '', valor: '' }])}
            data-testid="alta-anadir-eje"
          >
            <Plus size={14} /> Añadir eje
          </button>

          {errorAlta && (
            <p className="text-error text-sm font-inter" data-testid="alta-error">
              {errorAlta}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button className="btn-ghost" onClick={() => setCreandoEn(null)}>
              Cancelar
            </button>
            <button className="btn-gold" onClick={crearVariante} data-testid="alta-guardar">
              Crear variante
            </button>
          </div>
        </div>
      </Modal>

      {/* ─── Baja ─── */}
      <Confirm
        open={!!desactivando}
        onClose={() => setDesactivando(null)}
        onConfirm={() => desactivando && cambiarActiva(desactivando, false)}
        title="Desactivar variante"
        message="Deja de ofrecerse en la tienda, pero no se borra: su stock y su historial se conservan. Se puede volver a activar cuando quieras."
        confirmLabel="Desactivar"
        danger
      />
    </div>
  );
}
